import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Textarea } from '@components';

interface VariableField {
  id: string;
  name: string;
  description: string;
}

interface VariableExtractionBuilderProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

export const VariableExtractionBuilder: React.FC<VariableExtractionBuilderProps> = ({
  value,
  onChange,
  error,
}) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState<VariableField[]>([]);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonError, setJsonError] = useState<string>('');

  // Parse initial value
  useEffect(() => {
    if (value && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        if (parsed.schema && parsed.schema.properties) {
          const parsedFields: VariableField[] = Object.entries(
            parsed.schema.properties as Record<string, { description?: string }>
          ).map(([key, prop]) => ({
            id: Math.random().toString(36).substr(2, 9),
            name: key,
            description: prop.description || '',
          }));
          setFields(parsedFields);
        }
      } catch (e) {
        // If parsing fails, keep empty fields
        console.warn('Failed to parse variable extraction schema:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate JSON from fields
  const generateJSON = (currentFields: VariableField[]): string => {
    if (currentFields.length === 0) {
      return '';
    }

    const properties: Record<string, { type: string; description: string }> = {};

    currentFields.forEach(field => {
      if (field.name.trim()) {
        properties[field.name.trim()] = {
          type: 'string',
          description: field.description.trim() || `${field.name} value`,
        };
      }
    });

    const schema = {
      schema: {
        type: 'object',
        properties,
      },
    };

    return JSON.stringify(schema, null, 2);
  };

  // Update parent component when fields change
  useEffect(() => {
    if (!jsonMode) {
      const json = generateJSON(fields);
      onChange(json);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, jsonMode]);

  const addField = () => {
    const newField: VariableField = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      description: '',
    };
    setFields([...fields, newField]);
  };

  const removeField = (id: string) => {
    setFields(fields.filter(field => field.id !== id));
  };

  const updateField = (id: string, updates: Partial<VariableField>) => {
    setFields(fields.map(field => (field.id === id ? { ...field, ...updates } : field)));
  };

  const toggleJsonMode = () => {
    if (!jsonMode) {
      // Switching to JSON mode
      const json = generateJSON(fields);
      onChange(json);
      setJsonMode(true);
    } else {
      // Switching back to form mode
      try {
        const parsed = JSON.parse(value || '{}');
        if (parsed.schema && parsed.schema.properties) {
          const parsedFields: VariableField[] = Object.entries(
            parsed.schema.properties as Record<string, { description?: string }>
          ).map(([key, prop]) => ({
            id: Math.random().toString(36).substr(2, 9),
            name: key,
            description: prop.description || '',
          }));
          setFields(parsedFields);
          setJsonError('');
        }
        setJsonMode(false);
      } catch (e) {
        setJsonError(t('agents.invalidJsonFormat'));
      }
    }
  };

  const handleJsonChange = (newValue: string) => {
    onChange(newValue);
    setJsonError('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('agents.variableExtraction')}
        </label>
        <Button
          variant="secondary"
          size="sm"
          onClick={toggleJsonMode}
          className="text-xs"
          leftIcon={
            <span className="material-icons text-sm">{jsonMode ? 'edit_note' : 'code'}</span>
          }
        >
          {jsonMode ? t('agents.switchToFormMode') : t('agents.switchToJsonMode')}
        </Button>
      </div>

      {jsonMode ? (
        <div>
          <Textarea
            className="w-full font-mono text-sm"
            rows={12}
            value={value || ''}
            onChange={e => handleJsonChange(e.target.value)}
            placeholder={`{
  "schema": {
    "type": "object",
    "properties": {
      "name": {
        "type": "string",
        "description": "Vollständiger Name der Person"
      }
    }
  }
}`}
            state={error || jsonError ? 'error' : 'default'}
            errorText={error || jsonError}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {t('agents.defineJsonSchema')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {fields.length === 0 && (
            <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
              <span className="material-icons text-gray-400 text-4xl mb-2">add_circle_outline</span>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('agents.noVariablesDefined')}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {t('agents.clickAddVariableToStart')}
              </p>
            </div>
          )}

          {fields.map((field, index) => (
            <div
              key={field.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3"
            >
              <div className="flex items-start justify-between">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t('agents.variable')} {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeField(field.id)}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                  aria-label={t('agents.removeVariable')}
                >
                  <span className="material-icons text-sm">close</span>
                </button>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('agents.variableName')} *
                </label>
                <Input
                  value={field.name}
                  onChange={e => updateField(field.id, { name: e.target.value })}
                  placeholder={t('agents.variableNamePlaceholder')}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('agents.description')}
                </label>
                <Textarea
                  value={field.description}
                  onChange={e => updateField(field.id, { description: e.target.value })}
                  placeholder={t('agents.descriptionPlaceholder')}
                  rows={2}
                  className="w-full text-sm"
                />
              </div>
            </div>
          ))}

          <Button
            variant="secondary"
            onClick={addField}
            className="w-full"
            leftIcon={<span className="material-icons text-sm">add</span>}
          >
            {t('agents.addVariable')}
          </Button>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          {fields.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-start">
                <span className="material-icons text-blue-500 mr-2 text-sm mt-0.5">info</span>
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-1">{t('agents.variableExtractionInfo')}</p>
                  <p>{t('agents.variableExtractionInfoDetails')}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
